/**
 * PractitionerHeader — Top bar with brand title, role badge, notification bell,
 * and practitioner avatar.
 *
 * Gradient text for the app title:
 *   - Web: CSS background-clip trick via inline style
 *   - Native: solid #7C3AED (gradient text requires MaskedView; not worth the dep)
 *
 * Accessibility touch targets meet 44×44px minimum (WCAG 2.5.5).
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { ClientAvatar } from './ClientAvatar';

const ICON_HIT_SLOP = { top: 4, bottom: 4, left: 4, right: 4 };

const APP_TITLE_STYLE =
  Platform.OS === 'web'
    ? ({
        background: 'linear-gradient(135deg, #EC4899 0%, #A78BFA 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      } as any)
    : { color: '#7C3AED' };

interface PractitionerHeaderProps {
  practitionerName: string;
  practitionerInitials: string;
  unreadNotificationCount: number;
  onNotificationsPress: () => void;
  onAvatarPress: () => void;
  t: TFunction<'practitioner'>;
}

export const PractitionerHeader: React.FC<PractitionerHeaderProps> = ({
  practitionerName,
  practitionerInitials,
  unreadNotificationCount,
  onNotificationsPress,
  onAvatarPress,
  t,
}) => {
  return (
    <View style={styles.container}>
      {/* Left: app title + role badge */}
      <View style={styles.leftGroup}>
        <Text
          style={[styles.appTitle, APP_TITLE_STYLE]}
          accessibilityRole="header"
        >
          {t('header.app_title')}
        </Text>
        <View style={styles.roleBadge}>
          <Text style={styles.roleBadgeText}>{t('header.role_badge')}</Text>
        </View>
      </View>

      {/* Right: notification bell + avatar */}
      <View style={styles.rightGroup}>
        <Pressable
          onPress={onNotificationsPress}
          style={styles.bellButton}
          accessibilityRole="button"
          accessibilityLabel={t('accessibility.notifications')}
          accessibilityValue={
            unreadNotificationCount > 0
              ? { text: String(unreadNotificationCount) }
              : undefined
          }
          hitSlop={ICON_HIT_SLOP}
        >
          <Ionicons name="notifications-outline" size={20} color="#4B5563" />
          {unreadNotificationCount > 0 && (
            <View style={styles.notifDot} />
          )}
        </Pressable>

        <Pressable
          onPress={onAvatarPress}
          style={styles.avatarButton}
          accessibilityRole="button"
          accessibilityLabel={t('accessibility.profile', {
            name: practitionerName,
          })}
          hitSlop={ICON_HIT_SLOP}
        >
          <ClientAvatar initials={practitionerInitials} gradientIndex={0} size={36} />
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    zIndex: 50,
  } as ViewStyle,
  leftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  appTitle: {
    fontSize: 18,
    fontWeight: '800',
  },
  roleBadge: {
    backgroundColor: '#EDE9FE',
    borderRadius: 9999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  roleBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#7C3AED',
  },
  rightGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  bellButton: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notifDot: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#EC4899',
  },
  avatarButton: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
